import {
    Task,
    TaskGroupBy,
    TaskPriority,
    TaskStatus,
} from "@common/types/task";
import { TaskSortValue } from "@common/types/views/tasksView";

type SortKeyValue = string | number | null | undefined;

enum SortDirection {
    ASC = 1,
    DESC = -1,
}

enum NullsPosition {
    FIRST = "first",
    LAST = "last",
}

type ClientNamesById = ReadonlyMap<string, string>;

interface TaskSortSpec {
    getKey: (task: Task, clientNamesById: ClientNamesById) => SortKeyValue;
    direction: SortDirection;
    nulls: NullsPosition;
}

const PRIORITY_RANKS: Record<TaskPriority, number> = {
    [TaskPriority.URGENT]: 5,
    [TaskPriority.HIGH]: 4,
    [TaskPriority.MEDIUM]: 3,
    [TaskPriority.LOW]: 2,
};
const NO_PRIORITY_RANK = 1;

const STATUS_RANKS: Record<TaskStatus, number> = {
    [TaskStatus.TODO]: 1,
    [TaskStatus.IN_PROGRESS]: 2,
    [TaskStatus.BLOCKED]: 3,
    [TaskStatus.DONE]: 4,
    [TaskStatus.CANCELLED]: 5,
};

const getPriorityRank = (task: Task) =>
    task.priority ? PRIORITY_RANKS[task.priority] : NO_PRIORITY_RANK;

const asc = (
    getKey: TaskSortSpec["getKey"],
    nulls = NullsPosition.LAST,
): TaskSortSpec => ({ getKey, direction: SortDirection.ASC, nulls });

const desc = (
    getKey: TaskSortSpec["getKey"],
    nulls = NullsPosition.FIRST,
): TaskSortSpec => ({ getKey, direction: SortDirection.DESC, nulls });

const getTitle = (task: Task) => task.title;
const getDueDate = (task: Task) => task.dueDate;
const getEstimate = (task: Task) => task.estimatedTime;
const getStatusRank = (task: Task) => STATUS_RANKS[task.status];
const getCreatedAt = (task: Task) => task.createdAt.getTime();
const getUpdatedAt = (task: Task) => task.updatedAt.getTime();
const getCompletedAt = (task: Task) => task.lastDoneAt?.getTime();
const getCreatedByName = (task: Task) => task.createdBy?.name;
const getAssigneeName = (task: Task) => task.assignee?.name;
const getCompletedByName = (task: Task) => task.lastDoneBy?.name;
const getClientName = (task: Task, clientNamesById: ClientNamesById) =>
    clientNamesById.get(task.workspaceId);

const SORT_SPECS: Record<TaskSortValue, TaskSortSpec> = {
    [TaskSortValue.NAME_ASC]: asc(getTitle),
    [TaskSortValue.NAME_DESC]: desc(getTitle),
    [TaskSortValue.DUE_DATE_ASC]: asc(getDueDate, NullsPosition.FIRST),
    [TaskSortValue.DUE_DATE_DESC]: desc(getDueDate, NullsPosition.LAST),
    [TaskSortValue.ESTIMATE_ASC]: asc(getEstimate, NullsPosition.FIRST),
    [TaskSortValue.ESTIMATE_DESC]: desc(getEstimate, NullsPosition.LAST),
    [TaskSortValue.PRIORITY_ASC]: asc(getPriorityRank),
    [TaskSortValue.PRIORITY_DESC]: desc(getPriorityRank),
    [TaskSortValue.STATUS_ASC]: asc(getStatusRank),
    [TaskSortValue.STATUS_DESC]: desc(getStatusRank),
    [TaskSortValue.CREATED_AT_ASC]: asc(getCreatedAt),
    [TaskSortValue.CREATED_AT_DESC]: desc(getCreatedAt),
    [TaskSortValue.CREATED_BY_ASC]: asc(getCreatedByName),
    [TaskSortValue.CREATED_BY_DESC]: desc(getCreatedByName, NullsPosition.LAST),
    [TaskSortValue.ASSIGNEE_ASC]: asc(getAssigneeName),
    [TaskSortValue.ASSIGNEE_DESC]: desc(getAssigneeName, NullsPosition.LAST),
    [TaskSortValue.UPDATED_AT_ASC]: asc(getUpdatedAt),
    [TaskSortValue.UPDATED_AT_DESC]: desc(getUpdatedAt),
    [TaskSortValue.COMPLETED_AT_ASC]: asc(getCompletedAt),
    [TaskSortValue.COMPLETED_AT_DESC]: desc(getCompletedAt, NullsPosition.LAST),
    [TaskSortValue.COMPLETED_BY_ASC]: asc(getCompletedByName),
    [TaskSortValue.COMPLETED_BY_DESC]: desc(
        getCompletedByName,
        NullsPosition.LAST,
    ),
    [TaskSortValue.CLIENT_ASC]: asc(getClientName),
    [TaskSortValue.CLIENT_DESC]: desc(getClientName, NullsPosition.LAST),
};

const CLIENT_SORTS = new Set<TaskSortValue>([
    TaskSortValue.CLIENT_ASC,
    TaskSortValue.CLIENT_DESC,
]);

export const isClientSort = (sort: TaskSortValue | undefined): boolean =>
    sort !== undefined && CLIENT_SORTS.has(sort);

const NO_CLIENT_NAMES: ClientNamesById = new Map();

const DUE_DATE_SORTS = new Set<TaskSortValue>([
    TaskSortValue.DUE_DATE_ASC,
    TaskSortValue.DUE_DATE_DESC,
]);

const DUE_DATE_GROUP_SPEC = asc(getDueDate);

const compareKeys = (
    left: SortKeyValue,
    right: SortKeyValue,
    { direction, nulls }: TaskSortSpec,
) => {
    const isLeftNull = left === null || left === undefined;
    const isRightNull = right === null || right === undefined;
    if (isLeftNull || isRightNull) {
        if (isLeftNull && isRightNull) {
            return 0;
        }
        const nullOrder = nulls === NullsPosition.FIRST ? -1 : 1;
        return isLeftNull ? nullOrder : -nullOrder;
    }
    if (typeof left === "string" && typeof right === "string") {
        return left.localeCompare(right) * direction;
    }
    return (Number(left) - Number(right)) * direction;
};

const compareIds = (left: Task, right: Task) => {
    if (left.id === right.id) {
        return 0;
    }
    return left.id < right.id ? -1 : 1;
};

interface CompareTasksParams {
    sort?: TaskSortValue;
    groupBy?: TaskGroupBy;
    clientNamesById?: ClientNamesById;
}

/**
 * Client-side mirror of the backend `TaskSortingInternalService.applySort`,
 * used to merge task lists fetched separately per workspace into one order.
 */
export function compareTasks({
    sort,
    groupBy,
    clientNamesById = NO_CLIENT_NAMES,
}: CompareTasksParams) {
    const isGroupedByDueDate = groupBy === TaskGroupBy.DUE_DATE;
    const specs = [
        ...(isGroupedByDueDate ? [DUE_DATE_GROUP_SPEC] : []),
        ...(sort && !(isGroupedByDueDate && DUE_DATE_SORTS.has(sort))
            ? [SORT_SPECS[sort]]
            : []),
    ];

    return (left: Task, right: Task) => {
        for (const spec of specs) {
            const order = compareKeys(
                spec.getKey(left, clientNamesById),
                spec.getKey(right, clientNamesById),
                spec,
            );
            if (order !== 0) {
                return order;
            }
        }
        return compareIds(left, right);
    };
}
